import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, CheckCircle2, AlertTriangle, Loader2, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtHM } from "@/lib/utils";
import { FileInput } from "@/components/FileInput";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

function UnmatchedList({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-500/10 p-3" data-testid={testid}>
      <p className="text-sm font-medium text-amber-900 dark:text-amber-300 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" /> {title} (no se importarán)
      </p>
      <ul className="mt-2 text-xs text-amber-900 dark:text-amber-200 space-y-0.5 max-h-48 overflow-auto">
        {items.map((x) => <li key={x.name} className="break-all">{x.name} · {x.rows} filas · {fmtHM(x.hours)}</li>)}
      </ul>
    </div>
  );
}

function PeopleTable({ people }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm" data-testid="hours-import-people">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="py-2">Persona</th><th>Periodo</th><th className="text-right">Filas</th>
            <th className="text-right">Horas</th><th className="text-right">Proyectos</th><th className="text-right">Sustituye</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.user_id} className="border-b border-border/60" data-testid={`hours-person-${p.user_id}`}>
              <td className="py-2 font-medium">{p.user_name}</td>
              <td className="tabular-nums text-xs">{fmtDate(p.date_from)} → {fmtDate(p.date_to)}</td>
              <td className="text-right tabular-nums">{p.rows}</td>
              <td className="text-right tabular-nums">{fmtHM(p.hours)}</td>
              <td className="text-right tabular-nums">{p.projects}</td>
              <td className="text-right text-muted-foreground tabular-nums">{p.replaced} previas</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Summary({ res }) {
  return (
    <div className="mt-6 rounded-lg border border-primary/20 bg-primary/[0.04] p-4" data-testid="hours-import-summary">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        {res.dry_run ? <Search className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        {res.dry_run ? "Vista previa (aún no se ha guardado nada)" : "Importación completada"}
      </div>
      <p className="mt-2 text-sm" data-testid="hours-import-totals">
        <b>{res.valid_rows}</b> de {res.total_rows} filas válidas · <b>{fmtHM(res.valid_hours)}</b>
        {!res.dry_run && <span> · <b>{res.imported}</b> registros guardados</span>}
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        Proyectos encontrados por nombre: {res.matched_by_name} · por nº de proyecto: {res.matched_by_ref} · con fase/hito: {res.with_phase}
      </p>
      <PeopleTable people={res.people} />
      <UnmatchedList title="Proyectos no encontrados" items={res.unmatched_projects} testid="hours-unmatched-projects" />
      <UnmatchedList title="Personas no encontradas" items={res.unmatched_users} testid="hours-unmatched-users" />
      {res.bad_dates > 0 && <p className="text-xs text-amber-700 mt-3">{res.bad_dates} filas sin fecha válida (se omiten).</p>}
    </div>
  );
}

export const HoursExcelImport = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const send = async (dryRun) => {
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const { data } = await api.post("/time-entries/import-excel", fd, {
        params: { dry_run: dryRun }, headers: { "Content-Type": "multipart/form-data" },
      });
      setRes(data);
      if (!dryRun) toast.success(`Horas importadas: ${data.imported} registros`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo leer el Excel de horas");
    } finally { setLoading(false); }
  };

  return (
    <Card className="border-border shadow-none p-6 max-w-3xl" data-testid="hours-import-section">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
          <Clock className="h-6 w-6" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-lg">Horas del equipo por proyecto (Excel)</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            Sube el Excel de horas exportado de Teamleader (columnas <b>Fecha</b>, <b>Duración (en minutos)</b>, <b>Usuario</b>,
            <b> Fase</b>, <b>Proyecto</b>, <b>Descripción</b>). Cada fila va al proyecto con el mismo <b>nombre completo</b> y a la persona
            por su nombre. Al volver a subirlo se <b>sustituyen</b> las horas importadas de esa persona en el periodo del archivo;
            las registradas a mano en la app no se tocan.
          </p>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <FileInput file={file} onFile={(f) => { setFile(f); setRes(null); }} accept=".xlsx,.xls" testid="hours-import-file" />
        <Button variant="outline" className="gap-2" disabled={!file || loading} onClick={() => send(true)} data-testid="hours-import-preview">
          {loading && !res?.dry_run ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Analizar Excel
        </Button>
        {res?.dry_run && res.valid_rows > 0 && (
          <Button className="gap-2" disabled={loading} onClick={() => send(false)} data-testid="hours-import-confirm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {loading ? "Importando…" : `Importar ${res.valid_rows} registros`}
          </Button>
        )}
      </div>
      {res && <Summary res={res} />}
    </Card>
  );
};
